import { Link } from 'react-router';
import { Input } from '@/components/ui/input';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { ROUTE } from '@/routes/constants';
import { Theme, ThemeColors } from './login-theme';
import { EmailIcon, LockIcon, EyeIcon, LoginArrowIcon } from './login-icons';
import { useLogin } from './use-login';
import MobileLandscapeAuthShell from './mobile-landscape-auth-shell';

// 手机横屏(844×390)登录页 = 共享壳(MobileLandscapeAuthShell) + 右侧登录表单列。
// 壳负责卡片/顶栏/主题·语言/logo/FitScale;本组件只负责右侧登录表单(舞台坐标 1:1)。
// 表单行为复用 useLogin(与桌面/竖屏移动端完全一致)。

interface Props {
  theme: Theme;
  setTheme: (t: Theme) => void;
  colors: ThemeColors;
}

export default function MobileLandscapeLogin({ theme, setTheme, colors }: Props) {
  const {
    t, loginMessage, isLoading, isEmailInvalid, isPswInvalid,
    showPassword, setShowPassword, setEmailInvalid, setPswInvalid, onLogin,
  } = useLogin();

  const inputStyle: React.CSSProperties = {
    width: '100%', height: 29, borderRadius: 14.5, background: colors.inputBg, border: 0,
    fontSize: 11, color: '#1F1F1F',
  };
  const labelStyle: React.CSSProperties = { position: 'absolute', fontSize: 12, color: '#575C60', lineHeight: '12px' };

  return (
    <MobileLandscapeAuthShell theme={theme} setTheme={setTheme} colors={colors}>
      {/* 右侧登录表单 */}
      <form action={onLogin}>
        <LoadingOverlay isLoading={isLoading} />

        {/* 标题 + 副标题(在表单列 524..727 内居中) */}
        <div style={{ position: 'absolute', left: 524, top: 73, width: 203, textAlign: 'center', fontSize: 20, fontWeight: 600, lineHeight: '20px', color: '#2A2F32', letterSpacing: '-0.01em' }}>
          {t('user.welcome-back')}
        </div>
        <div style={{ position: 'absolute', left: 524, top: 97, width: 203, textAlign: 'center', fontSize: 12, color: '#575C60', lineHeight: '12px' }}>
          {t('user.welcome-sub')}
        </div>
        {loginMessage && (
          <div style={{ position: 'absolute', left: 524, top: 112, width: 203, textAlign: 'center', fontSize: 11, color: '#EF4444', lineHeight: '11px' }}>{loginMessage}</div>
        )}

        {/* 電子郵箱 */}
        <label htmlFor="ml-email" style={{ ...labelStyle, left: 524, top: 126 }}>{t('user.email')}</label>
        <div style={{ position: 'absolute', left: 524, top: 146, width: 203, height: 29 }}>
          <EmailIcon aria-hidden="true" style={{ position: 'absolute', left: 12, top: 8, width: 13, height: 13, zIndex: 1, color: '#575C60' }} />
          <Input
            id="ml-email" name="email" type="email" placeholder="scholar@atelier.edu.hk" required
            onInvalid={(e: any) => { setEmailInvalid(true); e.target.setCustomValidity(t('user.email-error')); }}
            onInput={(e: any) => { e.target.setCustomValidity(''); }}
            className="pl-[30px] pr-3 placeholder:text-[rgba(115,119,123,0.5)] focus-visible:ring-2"
            style={{ ...inputStyle, outline: isEmailInvalid ? '2px solid #F87171' : undefined, ['--tw-ring-color' as any]: colors.link + '66' }}
          />
        </div>

        {/* 密碼 + 忘記密碼 */}
        <label htmlFor="ml-password" style={{ ...labelStyle, left: 524, top: 185 }}>{t('user.password')}</label>
        <Link to={ROUTE.REQUEST_RESET_PASSWORD} style={{ position: 'absolute', left: 524, top: 185, width: 203, textAlign: 'right', fontSize: 12, color: colors.link, lineHeight: '12px' }} className="hover:underline">
          {t('user.forgot-password')}?
        </Link>
        <div style={{ position: 'absolute', left: 524, top: 204, width: 203, height: 28 }}>
          <LockIcon aria-hidden="true" style={{ position: 'absolute', left: 13, top: 8, width: 9, height: 12, zIndex: 1, color: '#575C60' }} />
          <Input
            id="ml-password" name="password" type={showPassword ? 'text' : 'password'} required
            pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$"
            onInvalid={(e: any) => { setPswInvalid(true); e.target.setCustomValidity(t('user.password-error')); }}
            onInput={(e: any) => { e.target.setCustomValidity(''); }}
            className="pl-[30px] pr-[30px] placeholder:text-[rgba(115,119,123,0.5)] focus-visible:ring-2"
            style={{ ...inputStyle, height: 28, outline: isPswInvalid ? '2px solid #F87171' : undefined, ['--tw-ring-color' as any]: colors.link + '66' }}
          />
          <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 12, top: 8, width: 18, height: 12 }}>
            <EyeIcon aria-hidden="true" style={{ width: 18, height: 12, color: '#73777B' }} />
          </button>
        </div>

        {/* 登录按钮 */}
        <button type="submit" style={{ position: 'absolute', left: 524, top: 243, width: 203, height: 35, borderRadius: 17.5, background: colors.buttonBg, color: colors.buttonText, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 500, border: 0, cursor: 'pointer' }} className="transition-[filter] hover:brightness-[0.97]">
          {t('user.sign-in-action')}
          <LoginArrowIcon aria-hidden="true" style={{ width: 12, height: 12, color: colors.buttonText }} />
        </button>

        {/* 分隔线 */}
        <div style={{ position: 'absolute', left: 524, top: 301, width: 203, height: 1, background: '#E5E7EB' }} />

        {/* 注册引导(表单列内居中) */}
        <div style={{ position: 'absolute', left: 524, top: 314, width: 203, textAlign: 'center', fontSize: 12, color: '#575C60', lineHeight: '20px' }}>
          {t('user.login-comment')}
          <Link to={ROUTE.SIGNUP} style={{ marginLeft: 6, fontWeight: 500, color: colors.link }} className="underline underline-offset-2">
            {t('user.sign-up')}
          </Link>
        </div>
      </form>
    </MobileLandscapeAuthShell>
  );
}
